import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import {
  Field,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  TagPickerProps,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Text
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';

import { Settlement } from '../../../../models/Entities/Settlements/Settlement';
import * as Functions from './SettlementSelectorFunctions';

const getSettlementLabel = (settlement: Settlement): string =>
  settlement.name ?? settlement.identifier;

const SettlementTagContent = ({ settlement }: { settlement: Settlement }) => (
  <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Text size={200} weight="semibold" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
      {settlement.name ?? settlement.identifier}
    </Text>
    {settlement.description && (
      <Text size={100} style={{ whiteSpace: 'normal', wordBreak: 'break-word', opacity: 0.75 }}>
        {settlement.description}
      </Text>
    )}
  </span>
);

const getInputPlaceholder = (selectedOptions: string[]): string =>
  selectedOptions.length === 0 ? 'Buscar liquidaciones...' : '';

const SettlementSelectorComponent = (props: Functions.Props) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [filterQuery, setFilterQuery] = useState<string>('');

  const isInternalUpdate = useRef(false);
  const previousSettlementsRef = useRef<Settlement[]>([]);

  // Cargar liquidaciones cuando cambia el protocolId
  useEffect(() => {
    if (props.protocolId) {
      props.GetSettlementsByProtocolId(props.protocolId);
    }
  }, [props.protocolId]);

  const areSettlementsEqual = useCallback((s1: Settlement[] | undefined, s2: Settlement[]): boolean => {
    if (s1?.length !== s2.length) return false;
    const ids1 = s1.map(s => s.id).sort((a, b) => a - b);
    const ids2 = s2.map(s => s.id).sort((a, b) => a - b);
    return ids1.every((id, index) => id === ids2[index]);
  }, []);

  // Sincronizar selectedOptions cuando selectedSettlements cambia desde el padre
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    if (!props.Settlements || props.Settlements.length === 0) return;

    if (props.selectedSettlements && props.selectedSettlements.length > 0) {
      const selectedIds = props.selectedSettlements
        .map((s: Settlement) => s.id?.toString())
        .filter((id): id is string => id !== undefined);

      const sortedIds = [...selectedIds].sort((a, b) => a.localeCompare(b));
      const sortedSelected = [...selectedOptions].sort((a, b) => a.localeCompare(b));
      if (JSON.stringify(sortedIds) !== JSON.stringify(sortedSelected)) {
        setSelectedOptions(selectedIds);
      }
    } else {
      setSelectedOptions([]);
    }
  }, [props.selectedSettlements, props.Settlements]);

  const onInputChange = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    setFilterQuery(e.currentTarget.value);
  }, []);

  const tagPickerOptions = useMemo(() => {
    if (!props.Settlements || props.Settlements.length === 0) return [];

    return props.Settlements.filter(
      (s: Settlement) =>
        !selectedOptions.includes(s.id.toString()) &&
        getSettlementLabel(s).toLowerCase().includes(filterQuery.toLowerCase())
    );
  }, [props.Settlements, selectedOptions, filterQuery]);

  const onOptionSelect: TagPickerProps['onOptionSelect'] = useCallback(
    (e, data) => {
      if (data.selectedOptions.length === 0) {
        setSelectedOptions([]);
        props.onChangeSelection?.([]);
        return;
      }

      let newSelectedOptions: string[];

      const isRemoving = data.selectedOptions.length < selectedOptions.length;

      if (isRemoving) {
        newSelectedOptions = data.selectedOptions;
      } else if (props.selectionLimit === 1) {
        newSelectedOptions = [data.value];
      } else {
        newSelectedOptions = data.selectedOptions;
        if (props.selectionLimit && newSelectedOptions.length > props.selectionLimit) {
          return;
        }
      }

      setSelectedOptions(newSelectedOptions);

      const selectedSettlements =
        newSelectedOptions.length > 0
          ? props.Settlements?.filter((s: Settlement) => newSelectedOptions.includes(s.id.toString())) || []
          : [];

      const shouldNotify =
        newSelectedOptions.length === 0
          ? previousSettlementsRef.current.length > 0
          : !areSettlementsEqual(selectedSettlements, previousSettlementsRef.current);

      if (shouldNotify) {
        previousSettlementsRef.current = selectedSettlements;
        isInternalUpdate.current = true;
        if (props.onChangeSelection) {
          props.onChangeSelection(selectedSettlements);
        }
      }
    },
    [props.selectionLimit, props.Settlements, props.onChangeSelection, areSettlementsEqual, selectedOptions]
  );

  if (props.isLoading) {
    return (
      <Field style={{ maxWidth: 400 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
          <Spinner size="tiny" label="Cargando liquidaciones..." />
        </div>
      </Field>
    );
  }

  if (props.failOnLoading && props.error) {
    return (
      <Field style={{ maxWidth: 400 }}>
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Error al cargar liquidaciones</MessageBarTitle>
            {props.error.ErrorMessage || 'No se pudieron cargar las liquidaciones. Por favor, intente nuevamente.'}
          </MessageBarBody>
        </MessageBar>
      </Field>
    );
  }

  const hasNoSettlements = !props.Settlements || props.Settlements.length === 0;

  return (
    <Field
      label="Liquidaciones"
      style={{ width: '100%' }}
      hint={props.selectionLimit ? `Puede seleccionar hasta ${props.selectionLimit} liquidación(es)` : undefined}
    >
      <TagPicker
        onOptionSelect={onOptionSelect}
        selectedOptions={selectedOptions}
        disabled={hasNoSettlements}
      >
        <TagPickerControl style={{ flexWrap: 'wrap' }}>
          <TagPickerGroup aria-label="Liquidaciones seleccionadas" style={{ flexWrap: 'wrap' }}>
            {selectedOptions.map((option) => {
              const settlement = props.Settlements?.find((s: Settlement) => s.id.toString() === option);
              if (!settlement) return null;

              return (
                <Tag
                  key={settlement.id}
                  shape="rounded"
                  value={option}
                  dismissible
                  dismissIcon={<DismissRegular />}
                  style={{ maxWidth: '100%', height: 'auto', alignItems: 'flex-start', paddingTop: 4, paddingBottom: 4 }}
                  primaryText={{ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }}
                >
                  <SettlementTagContent settlement={settlement} />
                </Tag>
              );
            })}
          </TagPickerGroup>
          <TagPickerInput
            aria-label="Seleccione liquidaciones"
            placeholder={
              hasNoSettlements ? 'No hay liquidaciones disponibles' : getInputPlaceholder(selectedOptions)
            }
            onKeyUp={onInputChange}
          />
        </TagPickerControl>
        <TagPickerList>
          {tagPickerOptions.length > 0 ? (
            tagPickerOptions.map((option) => (
              <TagPickerOption
                value={option.id.toString()}
                key={option.id}
                text={getSettlementLabel(option)}
              >
                {getSettlementLabel(option)}
              </TagPickerOption>
            ))
          ) : (
            <TagPickerOption value="no-options" text="">
              {hasNoSettlements ? 'No hay liquidaciones disponibles' : 'No hay más liquidaciones'}
            </TagPickerOption>
          )}
        </TagPickerList>
      </TagPicker>
    </Field>
  );
};

export default connect(
  Functions.mapStateToProps,
  Functions.mapDispatchToProps
)(SettlementSelectorComponent as any);
